import React, { forwardRef, ForwardRefRenderFunction, useImperativeHandle, useState } from "react";

import { Grid } from "@mui/material";
import { Color } from "Components/Form/Button";
import FieldView from "Components/Form/FieldView";
import Modal from "Components/Generic/Modal";
import { Team } from "Models/team";
import { useTranslation } from "react-i18next";
import { getEquipmentName } from "Util/getEquipmentName";

import useStyles from "./styles";

export interface HandleViewModalProps {
  handleOpenModal: (team?: Team) => void;
  handleCloseModal: (team?: Team) => void;
}

const ViewTeamModal: ForwardRefRenderFunction<HandleViewModalProps> = (_, ref) => {
  const { t: translate } = useTranslation();
  const classes = useStyles();

  const [team, setTeam] = useState<Team | null>(null);
  const [open, setOpen] = useState<boolean>(false);

  const handleOpenModal = (team?: Team) => {
    setOpen(true);
    if (team) setTeam(team);
  };

  const handleCloseModal = () => {
    setOpen(false);
    setTeam(null);
  };

  useImperativeHandle(ref, () => ({
    handleOpenModal,
    handleCloseModal,
  }));

  return (
    <Modal
      className={classes.modalWrapper}
      open={open}
      handleClose={handleCloseModal}
      title={`${translate("view")} '${team?.name}'`}
      showActionButtons
      actionButtonProps={{
        customColorCancel: Color.TEXT_INFO,
        classesCancel: classes.cancelText,
        textCancel: translate("close"),
        onCancel: () => handleCloseModal(),
      }}
    >
      <Grid container flex-directio="column" spacing={3}>
        <Grid item xs={12}>
          <FieldView
            label={translate("name")}
            value={team?.name || ""}
            labelClass={classes.label}
          />
        </Grid>
        <Grid item xs={12}>
          <FieldView
            label={translate("leaderUser")}
            value={team?.memberLeader?.fullName || ""}
            labelClass={classes.label}
          />
        </Grid>
        <Grid item xs={12}>
          <FieldView
            label={translate("staff")}
            value={team?.members
              ?.filter((member) => member.id !== team?.memberLeaderId)
              ?.map((member) => member?.fullName)
              ?.join(", ")}
            labelClass={classes.label}
          />
        </Grid>
        <Grid item xs={12}>
          <FieldView
            label={translate("equipments")}
            value={team?.fleets
              ?.map((equipment) => {
                const { fullText } = getEquipmentName(
                  equipment,
                  translate("general"),
                  translate("fleet")
                );

                return fullText;
              })
              ?.join(", ")}
            labelClass={classes.label}
          />
        </Grid>
      </Grid>
    </Modal>
  );
};

export default forwardRef(ViewTeamModal);
